// QA «reinvented» (хендофф design_handoff_qa_reinvented, 2026-08-23) —
// нарезка кадра и перевод-оверлей. Пока только для скрап-материала.
import { z } from 'zod';
import { QaAssetIdSchema } from './manager-qa-board';

// Геометрия обрезки, как её хранит экран: rect — доли ИСХОДНОГО кадра
// (полная рамка = {0,0,1,1}), guides/regions — в координатах интерфейса.
export const QaCropGeomSchema = z.object({
  rot: z.number().min(-45).max(45).default(0),
  rect: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1), w: z.number().min(0).max(1), h: z.number().min(0).max(1) }),
  guides: z.array(z.object({ axis: z.enum(['h', 'v']), pos: z.number().min(0).max(1) })).max(12).default([]),
  regions: z.array(z.string().max(16)).max(64).default([]),
});
export type QaCropGeom = z.infer<typeof QaCropGeomSchema>;

// Фрагмент рендерит клиент (canvas) — воркер без растровых библиотек.
export const QaSplitFragmentSchema = z.object({
  key: z.string().max(16).default(''),      // ключ области ('0:1') или 'rect'
  full_b64: z.string().min(100).max(16_000_000),
  thumb_b64: z.string().min(100).max(2_000_000),
  width: z.number().int().positive().max(20000),
  height: z.number().int().positive().max(20000),
});

export const PothosSplitAssetReqSchema = z.object({
  asset_id: QaAssetIdSchema,
  geom: QaCropGeomSchema,
  fragments: z.array(QaSplitFragmentSchema).min(1).max(24),
});
export type PothosSplitAssetReq = z.infer<typeof PothosSplitAssetReqSchema>;
export const PothosSplitAssetResSchema = z.object({
  ok: z.boolean(),
  // 1 фрагмент — тот же asset_id (замена на месте); N — новые дети.
  asset_ids: z.array(z.string()),
  replaced_in_place: z.boolean(),
  // Сколько прежних детей этого кадра снято перед новой нарезкой
  // (`split-redo`); 0 — первая нарезка.
  retired: z.number().int().default(0),
});
export type PothosSplitAssetRes = z.infer<typeof PothosSplitAssetResSchema>;

// «Снять обрезку»: вернуть детей в rejected/удалить и родителя в qa нельзя
// безопасно (байты родителя при замене на месте перезаписаны) — поэтому
// замена на месте необратима, а у N-детей откат = родитель обратно в qa,
// дети → rejected ('split-undo'). Экран показывает это честно.
export const PothosUnsplitAssetReqSchema = z.object({ asset_id: QaAssetIdSchema });
export const PothosUnsplitAssetResSchema = z.object({ ok: z.boolean(), reverted: z.number().int() });

// Перевод поверх текста: боксы слов/строк + перевод на язык QA.
export const QaTrBoxSchema = z.object({
  x: z.number(), y: z.number(), w: z.number(), h: z.number(),  // доли кадра 0…1
  text: z.string(),
  tr: z.string(),
});
export type QaTrBox = z.infer<typeof QaTrBoxSchema>;
export const PothosTranslateAssetReqSchema = z.object({
  asset_id: QaAssetIdSchema,
  lang: z.enum(['ru', 'uk', 'pt', 'en']).optional(),
  // Уменьшенная копия кадра (≤1600px, JPEG) от клиента — модель получает
  // её вместо оригинала из R2: в разы быстрее и не упирается в лимит
  // размера запроса. Без неё воркер берёт оригинал.
  image_b64: z.string().max(4_000_000).optional(),
});
export const PothosTranslateAssetResSchema = z.object({
  ok: z.boolean(),
  lang: z.string(),
  boxes: z.array(QaTrBoxSchema),
});
export type PothosTranslateAssetRes = z.infer<typeof PothosTranslateAssetResSchema>;
